// b17: Homey Pro, förmågornas kopplingar och lådorna för hemligheter, mellan appen, molnet och
// navet.
//
//   - Homey läses bara: navet hämtar enheterna och värdena med Homeys lokala Web API (GET med en
//     API-nyckel som Bearer), och anropar aldrig något som ändrar en förmåga, ett flöde eller en
//     inställning. Nyckeln skriver ägaren in i appen, där den förseglas till navet (lada.ts).
//   - Förmågorna i en enhet i Home Assistant eller Homey kopplas till Kolls signaler först när
//     ägaren har bekräftat det i guiden. Molnet säger kopplingarna till navet efter fyndets id, och
//     navet hittar själv entiteten eller förmågan i enheten: entiteternas id lämnar aldrig navet.
//   - GX-enhetens lösenord och Homeys nyckel går till navet i förseglade lådor, som Victrons
//     nycklar i b16. Molnet kan inte öppna dem, och tar bort dem när navet har tagit emot dem.

import type { Formaga } from "@koll/katalog";
import type { Forseglad } from "./lada.ts";
import type { SignalNamn } from "./signaler.ts";

/**
 * Homeys adress på platsens nät: ett namn eller en IPv4-adress med en port om det behövs (http),
 * en adress med https:// (till exempel Homeys egen under homeylocal.com), eller "mdns" för den
 * Homey som navet hittar med mDNS.
 */
export const HOMEY_ADRESS =
  /^(mdns|(https:\/\/)?(?=.{1,253}(:\d{1,5})?$)[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*(:\d{1,5})?)$/;

/** Adressen som betyder "den Homey som navet hittar med mDNS". */
export const HOMEY_MDNS = "mdns";

/** En API-nyckel ur Homeys webbapp: 20–400 synliga tecken, utan mellanslag. */
export const HOMEY_NYCKEL = /^[\x21-\x7e]{20,400}$/;

/** Behörigheten som nyckeln ska ha, och bara den: att se enheterna. */
export const HOMEY_BEHORIGHET = "devices.readonly";

/** Var nyckeln skapas, för guiden, Inställningar och JESPER.md. */
export const HOMEY_HJALP =
  "Skapa nyckeln i Homeys webbapp (my.homey.app): Inställningar → API-nycklar → Ny API-nyckel. Ge den ett namn, till exempel Båtkoll, och bocka bara i Visa enheter (devices.readonly), ingenting annat. Kopiera nyckeln och klistra in den här.";

/** Det molnet säger till navet om Homey, när platsen har en adress. */
export interface Homeyorder {
  adress: string;
}

/** En förmåga i en enhet, kopplad till Kolls signal. */
export interface Formagekoppling {
  /** Fyndets id för enheten i Home Assistant eller Homey. */
  fynd: string;
  formaga: Formaga;
  signal: SignalNamn;
  /** Instansen: ägarens namn för enheten, eller null. */
  instans: string | null;
  /**
   * Signalen har en instans per enhet. Utan ägarens namn sätter navet enhetens namn i Home
   * Assistant eller Homey.
   */
  perEnhet: boolean;
}

/** Ämnena för lådorna med hemligheter: GX-enhetens lösenord och Homeys nyckel. */
export const HEMLIGHETER = ["gx", "homey"] as const;

export type Hemlighet = (typeof HEMLIGHETER)[number];

/** En hemlighet i en förseglad låda, till navet. Ämnet är det som lådan är bunden till. */
export interface Hemlighetslada {
  id: string;
  amne: Hemlighet;
  lada: Forseglad;
}

/** Hemligheten i lådan för GX-enheten. Ett tomt lösenord tar bort det. */
export interface Gxinloggning {
  anvandare: string;
  losenord: string;
}

/** Hemligheten i lådan för Homey. En tom nyckel tar bort den. */
export interface Homeynyckel {
  nyckel: string;
}

/** Användarnamnet för MQTT på GX-enheten: 0–64 synliga tecken. */
export const GX_ANVANDARE = /^[\x21-\x7e]{0,64}$/;

/** Lösenordet för MQTT på GX-enheten: 1–128 tecken utan styrtecken. */
export const GX_LOSENORD = /^[^\p{Cc}]{1,128}$/u;

/** En hemlighets läge, i appen: ingen, på väg till navet, eller mottagen av navet. */
export type Hemlighetslage = "ingen" | "skickad" | "mottagen";

/** Inställningar → Homey, och guidens del för Homey. */
export interface Homeyinstallningar {
  adress: string | null;
  /** En Homey svarar på mDNS på platsens nät, och adressen kan vara "mdns". */
  forslag: boolean;
  kontakt: boolean | null;
  /** Nyckeln: ingen, skickad, mottagen. */
  nyckel: Hemlighetslage;
  /** Det navet säger om nyckeln: saknas, fel eller finns. Null: inget än. */
  nyckelNavet: "saknas" | "fel" | "finns" | null;
  /** Nyckelns behörigheter enligt Homey, eller null. */
  behorighet: "bara_lasning" | "mer_an_lasning" | "okand" | null;
  /** Antalet enheter som navet hör i Homey. */
  enheter: number;
  /** Navets publika nyckel för lådan, eller null när navet inte kan ta emot nycklar. */
  lada: string | null;
}
